import { useIsHydrated } from '@core/shared';
import { EmailListSend, EmailTemplateType, OptionsApi } from '@core/swagger-client';
import { Button, Dialog, ErrorMessage, Input, TextArea } from '@core/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewPanelOrganism } from '../../organism';

interface Props {
    listId: string;
    /** Past sends — used to warn when this list has already been sent a general e-mail. */
    existingSends: EmailListSend[];
    open: boolean;
    onClose: () => void;
    /** Called after a send is accepted (so the parent can refresh + close). */
    onSent: () => void;
}

/**
 * General-marketing send dialog for a list: edit the subject/body — pre-filled from the EmailList default
 * template — with a live HTML preview, then send. Webinar e-mails are handled by webinar campaigns, not here.
 */
export const SendEmailDialog = ({ listId, existingSends, open, onClose, onSent }: Props) => {
    const { t } = useTranslation();
    const isHydrated = useIsHydrated();

    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [seeded, setSeeded] = useState(false);

    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewBody, setPreviewBody] = useState('');
    const [previewedFor, setPreviewedFor] = useState<{ subject: string; body: string }>();

    const previewMutation = useMutation(OptionsApi.emailLists.previewTemplate());
    const sendMutation = useMutation(OptionsApi.emailLists.send());

    // The dialog now stays mounted across opens (so Dialog can restore focus on close),
    // so reset to a blank slate each time it (re)opens instead of relying on a fresh mount.
    useEffect(() => {
        if (!open) return;
        setSubject('');
        setBody('');
        setSeeded(false);
        setPreviewLoading(false);
        setPreviewSubject('');
        setPreviewBody('');
        setPreviewedFor(undefined);
        sendMutation.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Pre-fill from the EmailList default template, once.
    const { data: defaultTemplate } = useQuery(OptionsApi.emailLists.getDefaultTemplate(EmailTemplateType.EmailList, { enabled: open && !seeded }));
    useEffect(() => {
        if (!seeded && defaultTemplate) {
            setSubject(defaultTemplate.subject);
            setBody(defaultTemplate.body);
            setSeeded(true);
        }
    }, [seeded, defaultTemplate]);

    // Debounced live preview, mirroring the template editor.
    useEffect(() => {
        if (!subject || !body) {
            setPreviewLoading(false);
            setPreviewSubject('');
            setPreviewBody('');
            return;
        }
        if (previewMutation.isPending) return;
        if (previewedFor?.subject === subject && previewedFor?.body === body) return;

        setPreviewLoading(true);
        const handle = setTimeout(() => {
            previewMutation.mutate(
                { templateType: EmailTemplateType.EmailList, subject, body },
                {
                    onSuccess: (rendered) => {
                        setPreviewLoading(false);
                        setPreviewedFor({ subject, body });
                        setPreviewSubject(rendered.subject);
                        setPreviewBody(rendered.body);
                    },
                    onError: () => {
                        setPreviewLoading(false);
                        setPreviewedFor({ subject, body });
                        setPreviewSubject('');
                        setPreviewBody('');
                    }
                }
            );
        }, 500);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject, body, previewedFor, previewMutation.isPending]);

    const alreadySent = existingSends.some((send) => send.templateType === EmailTemplateType.EmailList);

    const handleSend = () =>
        sendMutation.mutate(
            { id: listId, request: { templateType: EmailTemplateType.EmailList, subject, body } },
            { onSuccess: onSent }
        );

    const sendDisabled = !isHydrated || sendMutation.isPending || !subject.trim() || !body.trim();

    return (
        <Dialog open={open} onClose={onClose} title={t('EmailLists.Send.Title')} widthClassName="max-w-5xl">
            {sendMutation.error && <ErrorMessage apiError={sendMutation.error} classNames="mb-4" />}

            <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 flex flex-col gap-4">
                    <Input
                        data-testid="email-list-send-subject"
                        label={t('EmailLists.Send.SubjectLabel')}
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                    />
                    <TextArea
                        data-testid="email-list-send-body"
                        label={t('EmailLists.Send.BodyLabel')}
                        value={body}
                        onChange={(event) => setBody(event.target.value)}
                        textAreaClassNames="min-h-[30vh] field-sizing-content"
                    />
                    {alreadySent && <ErrorMessage statusMessage={t('EmailLists.Send.AlreadySentWarning')} />}
                </div>

                <div className="flex-1 flex flex-col">
                    <PreviewPanelOrganism isLoading={previewLoading} subject={previewSubject} html={previewBody} />
                </div>
            </div>

            <div className="flex justify-end gap-x-4 mt-8">
                <Button data-testid="email-list-send-cancel" variant="warning" onClick={onClose}>
                    {t('Generic.Button.Cancel')}
                </Button>
                <Button data-testid="email-list-send-confirm" variant="success" isLoading={sendMutation.isPending} disabled={sendDisabled} onClick={handleSend}>
                    {t('EmailLists.Send.Confirm')}
                </Button>
            </div>
        </Dialog>
    );
};
